import { useState } from 'react'
import { useSendDatahubDefaultEvents } from 'src/hook/useSendDatahubDefaultEvents'
import {
  Action,
  ComponentName,
  ComponentSource,
  Entity,
  ProfileType,
  TryResult
} from 'src/utils/datahubDefaultEvent'
import { PostType } from 'src/pages/communityPost/communityPost.types'
import { NewsPostType } from 'src/hook/usePosts'
import { useSessionData } from 'src/hook/useSessionData'
import {
  FailureDescription,
  SendLoadHighlightsEventParams,
  SendOpenMessageBoxEventParams,
  SendOpenProfileEventParams,
  SendPostDetailEventParams,
  SendReactPostEventParams,
  SendReadActivitiesEventParams,
  UseTrackEventsProps
} from './useTrackEvents.types'

const useTrackEvents = ({ loggedUserUcode }: UseTrackEventsProps) => {
  const [sendingEvent, setSendingEvent] = useState(false)
  const { user } = useSessionData()
  const { dispatchDatahubDefaultEvents } = useSendDatahubDefaultEvents()

  const isLoggedUser = Boolean(user?.isLogged)

  const isSessionUserProfile = (userUcode?: string) => {
    return !userUcode || userUcode === loggedUserUcode
  }

  const sendOpenProfileEvent = ({
    componentName,
    userUcode,
    accessFromMention
  }: SendOpenProfileEventParams) => {
    if (isLoggedUser && !sendingEvent) {
      setSendingEvent(true)

      dispatchDatahubDefaultEvents({
        action: Action.CLICK_OPEN_PROFILE,
        entity: Entity.PROFILE,
        event: {
          componentName,
          screenName: ComponentName.CLUB_PROFILE,
          profileType: isSessionUserProfile(userUcode)
            ? ProfileType.MY_PROFILE
            : ProfileType.SOMEONE_PROFILE,
          profileUcode: userUcode || loggedUserUcode,
          accessFromMention
        }
      })

      setTimeout(() => {
        setSendingEvent(false)
      }, 10000)
    }
  }

  const sendOpenMessageBoxEvent = ({ userUcode }: SendOpenMessageBoxEventParams) => {
    if (isLoggedUser) {
      dispatchDatahubDefaultEvents({
        action: Action.CLICK_OPEN_MESSAGE_BOX,
        entity: Entity.PROFILE,
        event: {
          componentName: ComponentName.CLUB_PROFILE,
          screenName: ComponentName.CLUB_PROFILE,
          profileType: isSessionUserProfile(userUcode)
            ? ProfileType.MY_PROFILE
            : ProfileType.SOMEONE_PROFILE,
          profileUcode: userUcode
        }
      })
    }
  }

  const sendPostDetailEvent = (post: SendPostDetailEventParams) => {
    const postTypes = {
      [PostType.URL]: 'URL',
      [PostType.VIDEO]: 'VIDEO',
      [PostType.AUDIO]: 'AUDIO',
      [PostType.IMAGE]: 'IMAGE',
      [PostType.TEXT]: 'TEXT',
      [NewsPostType.LIVE]: 'WEBINAR'
    }

    const postType = post?.type ? postTypes[post.type] : 'TEXT'

    if (isLoggedUser) {
      dispatchDatahubDefaultEvents({
        action: Action.CLICK_POST_DETAIL,
        entity: Entity.PROFILE,
        event: {
          componentName: ComponentName.CLUB_PROFILE,
          screenName: ComponentName.CLUB_PROFILE,
          communityId: Number(post.communityId),
          postId: Number(post.id),
          postType
        }
      })
    }
  }

  const sendReactPostEvent = ({
    result,
    statusCode = 500,
    failureDescription = FailureDescription.ERROR_WHILE_FETCHING_DATA,
    userUcode
  }: SendReactPostEventParams) => {
    const statusCodeValue = result === TryResult.SUCCESSFUL ? 200 : statusCode
    const failureDescriptionValue =
      result === TryResult.SUCCESSFUL
        ? FailureDescription.NOT_FAILURE_RESULT_EVENT
        : failureDescription

    if (isLoggedUser) {
      dispatchDatahubDefaultEvents({
        action: Action.TRY_CREATE_REACT_POST,
        entity: Entity.PROFILE,
        event: {
          profileType: isSessionUserProfile(userUcode)
            ? ProfileType.MY_PROFILE
            : ProfileType.SOMEONE_PROFILE,
          profileUcode: userUcode,
          componentName: ComponentName.CLUB_PROFILE,
          componentSource: ComponentSource.POST_REACTION,
          screenName: ComponentName.CLUB_PROFILE,
          result,
          statusCode: statusCodeValue || 500,
          failureDescription: failureDescriptionValue
        }
      })
    }
  }

  const sendReadActivitiesEvent = ({
    contentType,
    itemsCount,
    result,
    componentSource,
    statusCode = 500,
    failureDescription = FailureDescription.ERROR_WHILE_FETCHING_DATA
  }: SendReadActivitiesEventParams) => {
    const statusCodeValue = result === TryResult.SUCCESSFUL ? 200 : statusCode
    const failureDescriptionValue =
      result === TryResult.SUCCESSFUL
        ? FailureDescription.NOT_FAILURE_RESULT_EVENT
        : failureDescription

    if (isLoggedUser) {
      dispatchDatahubDefaultEvents({
        action: Action.TRY_READ_ACTIVITIES,
        entity: Entity.PROFILE,
        event: {
          contentType,
          itemsCount,
          result,
          statusCode: statusCodeValue || 500,
          failureDescription: failureDescriptionValue,
          componentName: ComponentName.CLUB_PROFILE,
          componentSource,
          screenName: ComponentName.CLUB_PROFILE
        }
      })
    }
  }

  const sendClickAccessClassEvent = (productId: number) => {
    if (isLoggedUser) {
      dispatchDatahubDefaultEvents({
        action: Action.CLICK_ACCESS_CLASS,
        entity: Entity.PROFILE,
        event: {
          componentName: ComponentName.CLUB_PROFILE,
          screenName: ComponentName.CLUB_PROFILE,
          productId
        }
      })
    }
  }

  const sendClickAccessCommunityEvent = (communityId: number) => {
    if (isLoggedUser) {
      dispatchDatahubDefaultEvents({
        action: Action.CLICK_ACCESS_COMMUNITY,
        entity: Entity.PROFILE,
        event: {
          componentName: ComponentName.CLUB_PROFILE,
          screenName: ComponentName.CLUB_PROFILE,
          communityId
        }
      })
    }
  }

  const sendLoadHighlightsEvent = ({
    result,
    statusCode = 500,
    failureDescription = FailureDescription.ERROR_WHILE_FETCHING_DATA,
    userUcode,
    classCount = 0,
    commentCount = 0,
    postCount = 0
  }: SendLoadHighlightsEventParams) => {
    const statusCodeValue = result === TryResult.SUCCESSFUL ? 200 : statusCode
    const failureDescriptionValue =
      result === TryResult.SUCCESSFUL
        ? FailureDescription.NOT_FAILURE_RESULT_EVENT
        : failureDescription

    if (isLoggedUser) {
      dispatchDatahubDefaultEvents({
        action: Action.TRY_LOAD_HIGHLIGHTS,
        entity: Entity.PROFILE,
        event: {
          profileType: isSessionUserProfile(userUcode)
            ? ProfileType.MY_PROFILE
            : ProfileType.SOMEONE_PROFILE,
          profileUcode: userUcode || loggedUserUcode,
          componentName: ComponentName.HIGHLIGHTS_SECTION,
          screenName: ComponentName.CLUB_PROFILE,
          componentSource: ComponentSource.HIGHLIGHTS_SECTION,
          classCount,
          commentCount,
          postCount,
          result,
          statusCode: statusCodeValue || 500,
          failureDescription: failureDescriptionValue
        }
      })
    }
  }

  return {
    sendOpenProfileEvent,
    sendOpenMessageBoxEvent,
    sendPostDetailEvent,
    sendReactPostEvent,
    sendReadActivitiesEvent,
    sendClickAccessClassEvent,
    sendClickAccessCommunityEvent,
    sendLoadHighlightsEvent
  }
}

export default useTrackEvents
